import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import type {
  CreateHandbookPageRequest,
  DiscardHandbookDraftResponse,
  HandbookImport,
  HandbookImportResponse,
  HandbookPage,
  HandbookPageResponse,
  HandbookPageVersion,
  HandbookPageVersionsResponse,
  PublishHandbookPagesResponse,
  UpdateHandbookDraftRequest,
} from '@dreamai/types';
import type {
  CreateHandbookFillRequest,
  GenerateHandbookSetupResponse,
  HandbookDocument,
  HandbookDocumentsResponse,
  HandbookFill,
  HandbookFillResponse,
  HandbookFillResults,
  HandbookPageFill,
  HandbookPreviewResponse,
  HandbookSetup,
  HandbookSetupAnswers,
  HandbookSetupResponse,
  HandbookTreeNode,
  HandbookTreeResponse,
  SaveHandbookSetupRequest,
} from './handbookContract';
import { formatDate } from './organisationLabels';
import {
  findNode,
  handbookErrorMessage,
  httpStatus,
  parentCandidates,
} from './handbookTree';
import { HandbookConfirmModal } from './HandbookConfirmModal';
import { HandbookCreatePage, HandbookPageEditor } from './HandbookPageEditor';
import { assistantCopy } from './organisationAssistantCopy';
import {
  HandbookSetupModal,
  SetupFill,
  SetupPrefill,
} from './HandbookSetupModal';
import { SetupGoalId } from './handbookSetup';
import { HandbookFillError, awaitDocuments, awaitFill } from './handbookFills';
import { ReactComponent as BookOpenIcon } from '../../icons/book-open-01.svg';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as FolderIcon } from '../../icons/projects-folder.svg';
import { ReactComponent as FileIcon } from '../../icons/file-05.svg';
import { ReactComponent as HistoryIcon } from '../../icons/history.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import type { PullSource } from './HandbookDocumentPull';
import { HandbookSectionWizard, NewSection } from './HandbookSectionWizard';
import type { TakenTitle } from './handbookSetup';
import {
  IMPORT_POLL_INTERVAL_MS,
  isImportRunning,
  upsertImport,
} from './handbookImports';
import './organisation-handbook.scss';
import './handbook-setup.scss';

interface OrganisationHandbookProps {
  organisationId: string;
  /**
   * The organisation has a Team Assistant (its tab is shown); the copy names
   * it, otherwise it speaks of the members' own assistants.
   */
  teamAssistantsAvailable: boolean;
  /** Whether the Handbook tab is the one showing */
  visible: boolean;
  /**
   * Reports whether the tab may be shown at all: `true` once the tree call
   * has succeeded, `false` while loading or when the backend says the
   * feature is off (404) or the caller may not administer it (403).
   */
  onAvailabilityChange: (available: boolean) => void;
}

type Feedback = { type: 'success' | 'error'; message: string } | null;

type Tree = Pick<
  HandbookTreeResponse,
  'tree' | 'archived' | 'publishedCount' | 'draftCount'
>;

type Confirm =
  | { kind: 'discard'; page: HandbookPage }
  | { kind: 'archive'; page: HandbookPage }
  | { kind: 'restore'; slug: string; version: HandbookPageVersion };

const EMPTY_STATE_TEXT =
  'Your Handbook helps your individual and team secretaries answer questions about your company. Add details about policies, processes, or anything you commonly have to answer yourself.';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** The top-level page (section) a page sits under. */
const sectionOf = (nodes: HandbookTreeNode[], slug: string): string | null =>
  nodes.find((top) => findNode([top], slug))?.slug ?? null;

const allNodes = (nodes: HandbookTreeNode[]): HandbookTreeNode[] =>
  nodes.flatMap((n) => [n, ...allNodes(n.children)]);

/**
 * Pages listed under a section: everything beneath it, plus the section page
 * itself when it carries content of its own or has nothing beneath it.
 */
const sectionPages = (node: HandbookTreeNode) => {
  const descendants = allNodes(node.children);
  return descendants.length === 0 || node.hasContent
    ? [node, ...descendants]
    : descendants;
};

/** Every slug in use (sections, pages, archived pages), so new titles can be checked before creating anything */
const takenTitles = (tree: Tree | null): TakenTitle[] => {
  if (!tree) return [];
  const sectionOf = new Map(tree.tree.map((n) => [n.slug, n.title]));
  return [
    ...tree.tree.flatMap((n) => [
      { slug: n.slug, title: n.title, section: null },
      ...allNodes(n.children).map((c) => ({
        slug: c.slug,
        title: c.title,
        section: n.title,
      })),
    ]),
    ...tree.archived.map((a) => ({
      slug: a.slug,
      title: a.title,
      section: a.parentSlug ? (sectionOf.get(a.parentSlug) ?? null) : null,
    })),
  ];
};

const previewText = (markdown: string) =>
  markdown
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*[-+]\s+/gm, '')
    .replace(/[*_`>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

interface PagePreview {
  live: string;
  draft: string;
  createdAt: string | null;
}

type SectionTab = 'published' | 'drafts' | 'history';

interface SectionHistoryEntry {
  slug: string;
  version: HandbookPageVersion;
}

/** e.g. "06 Oct 2026, 14:22" */
const formatCreated = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

/**
 * Settings → Organisation → Handbook: the organisation's own reference pages
 * that Reve answers from. Mounted for admins on every tab so the tree call
 * (which doubles as the availability check) runs as soon as the organisation
 * loads; renders nothing until `visible`.
 */
const OrganisationHandbook: FC<OrganisationHandbookProps> = ({
  organisationId,
  teamAssistantsAvailable,
  visible,
  onAvailabilityChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisationId}/handbook`;
  const assistant = assistantCopy(teamAssistantsAvailable);

  const [tree, setTree] = useState<Tree | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, PagePreview>>({});
  const [tab, setTab] = useState<SectionTab>('drafts');
  const [addingSection, setAddingSection] = useState(false);
  const [fillingSection, setFillingSection] = useState<string | null>(null);
  const [createParent, setCreateParent] = useState<string | null>(null);
  const [page, setPage] = useState<HandbookPage | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sectionHistory, setSectionHistory] = useState<
    SectionHistoryEntry[] | null
  >(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [imports, setImports] = useState<HandbookImport[] | null>(null);
  const [setup, setSetup] = useState<HandbookSetup | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const fetchTree = useCallback(async (): Promise<Tree> => {
    const { data } = await api.get<HandbookTreeResponse>(
      `${base}/pages`,
      await authHeaders(),
    );
    return {
      tree: data.tree,
      archived: data.archived,
      publishedCount: data.publishedCount,
      draftCount: data.draftCount,
    };
  }, [base, authHeaders]);

  const fetchPage = useCallback(
    async (slug: string) => {
      const { data } = await api.get<HandbookPageResponse>(
        `${base}/pages/${encodeURIComponent(slug)}`,
        await authHeaders(),
      );
      return data.page;
    },
    [base, authHeaders],
  );

  const fetchVersions = useCallback(
    async (slug: string) => {
      const { data } = await api.get<HandbookPageVersionsResponse>(
        `${base}/pages/${encodeURIComponent(slug)}/versions`,
        await authHeaders(),
      );
      return data.versions;
    },
    [base, authHeaders],
  );

  const fetchImport = useCallback(
    async (importId: string) => {
      const { data } = await api.get<HandbookImportResponse>(
        `${base}/imports/${encodeURIComponent(importId)}`,
        await authHeaders(),
      );
      return data.import;
    },
    [base, authHeaders],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchTree();
        if (cancelled) return;
        setTree(next);
        onAvailabilityChange(true);
      } catch (err) {
        if (cancelled) return;
        const status = httpStatus(err);
        if (status !== 404 && status !== 403) {
          console.error('Error loading handbook:', err);
        }
        onAvailabilityChange(false);
      }
    })();
    return () => {
      cancelled = true;
      onAvailabilityChange(false);
    };
  }, [fetchTree, onAvailabilityChange]);

  const loadSetup = useCallback(async () => {
    try {
      const { data } = await api.get<HandbookSetupResponse>(
        `${base}/setup`,
        await authHeaders(),
      );
      setSetup(data.setup);
      const importId = data.setup?.importId;
      if (importId) {
        const imp = await fetchImport(importId);
        setImports((list) => upsertImport(list ?? [], imp));
      }
    } catch (err) {
      console.error('Error loading handbook setup:', err);
    }
  }, [base, authHeaders, fetchImport]);

  const treeLoaded = tree !== null;
  useEffect(() => {
    if (treeLoaded) loadSetup();
  }, [treeLoaded, loadSetup]);

  useEffect(() => {
    if (!selectedSlug) {
      setPage(null);
      setPageError(null);
      return;
    }
    let cancelled = false;
    setPage(null);
    setPageError(null);
    (async () => {
      try {
        const loaded = await fetchPage(selectedSlug);
        if (!cancelled) setPage(loaded);
      } catch (err) {
        console.error('Error loading handbook page:', err);
        if (!cancelled) {
          setPageError(handbookErrorMessage(err, 'Could not load this page.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSlug, fetchPage]);

  const activeSection =
    tree?.tree.find((n) => n.slug === selectedSection) ?? tree?.tree[0] ?? null;

  useEffect(() => {
    if (!activeSection) return;
    let cancelled = false;
    Promise.all(
      sectionPages(activeSection).map(async (node) => {
        try {
          const loaded = await fetchPage(node.slug);
          const preview: PagePreview = {
            live: previewText(loaded.live?.bodyMarkdown ?? ''),
            draft: previewText(loaded.draft?.bodyMarkdown ?? ''),
            createdAt:
              (loaded.draft ?? loaded.live)?.createdAt ??
              loaded.live?.publishedAt ??
              null,
          };
          return [node.slug, preview];
        } catch {
          return [node.slug, { live: '', draft: '', createdAt: null }];
        }
      }),
    ).then((entries) => {
      if (!cancelled)
        setPreviews((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => {
      cancelled = true;
    };
  }, [activeSection, fetchPage]);

  const activeSlug = activeSection?.slug;
  const activeHasPublished =
    !!activeSection && sectionPages(activeSection).some((n) => n.isPublished);
  useEffect(() => {
    setTab(activeHasPublished ? 'published' : 'drafts');
  }, [activeSlug, activeHasPublished]);

  useEffect(() => {
    if (tab !== 'history' || !activeSection) return;
    let cancelled = false;
    setSectionHistory(null);
    const sectionSlug = activeSection.slug;
    const slugs = [
      ...sectionPages(activeSection)
        .filter((n) => n.isPublished)
        .map((n) => n.slug),
      ...(tree?.archived ?? [])
        .filter((a) => a.parentSlug === sectionSlug || a.slug === sectionSlug)
        .map((a) => a.slug),
    ];
    Promise.all(
      slugs.map(async (slug) =>
        (await fetchVersions(slug))
          .filter((v) => v.publishedAt)
          .map((version) => ({ slug, version })),
      ),
    )
      .then((lists) => {
        if (cancelled) return;
        setSectionHistory(
          lists
            .flat()
            .sort((a, b) =>
              (b.version.publishedAt ?? '').localeCompare(
                a.version.publishedAt ?? '',
              ),
            ),
        );
      })
      .catch((err) => {
        console.error('Error loading handbook history:', err);
        if (!cancelled) setSectionHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, activeSection, tree?.archived, fetchVersions]);

  const loadDocuments = useCallback(async (): Promise<HandbookDocument[]> => {
    const { data } = await api.get<HandbookDocumentsResponse>(
      `${base}/documents`,
      await authHeaders(),
    );
    return data.documents;
  }, [base, authHeaders]);

  const uploadDocuments = async (
    files: File[],
  ): Promise<HandbookDocument[]> => {
    if (files.length === 0) return [];
    const headers = await authHeaders();
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    const { data } = await api.post<HandbookDocumentsResponse>(
      `${base}/documents`,
      form,
      {
        headers: { ...headers.headers, 'Content-Type': 'multipart/form-data' },
      },
    );
    return data.documents;
  };

  /**
   * Uploads `files` and waits until they have been read. Returns the new
   * document ids in file order, and the ids (given plus new) that can be
   * written from.
   */
  const readyDocuments = async (files: File[], documentIds: string[]) => {
    const uploaded = await uploadDocuments(files);
    const usable = await awaitDocuments(
      uploaded.map((d) => d.id),
      loadDocuments,
    );
    return {
      newDocumentIds: uploaded.map((d) => d.id),
      usableIds: [...documentIds, ...usable.map((d) => d.id)],
    };
  };

  const fetchFill = useCallback(
    async (fillId: string): Promise<HandbookFill> => {
      const { data } = await api.get<HandbookFillResponse>(
        `${base}/fills/${encodeURIComponent(fillId)}`,
        await authHeaders(),
      );
      return data.fill;
    },
    [base, authHeaders],
  );

  /** Starts a fill and waits for its results. */
  const runFill = async (
    request: CreateHandbookFillRequest,
  ): Promise<HandbookFillResults> => {
    const { data } = await api.post<HandbookFillResponse>(
      `${base}/fills`,
      request,
      await authHeaders(),
    );
    return awaitFill(data.fill, fetchFill);
  };

  const fillPages = async (
    titles: string[],
    documentIds: string[],
  ): Promise<Record<string, HandbookPageFill | null>> => {
    const results = await runFill({ kind: 'pages', titles, documentIds });
    if (results.kind !== 'pages') {
      throw new HandbookFillError(
        'Your pages could not be written. Try again.',
      );
    }
    return results.pages;
  };

  const fillOwnPages = async (
    files: File[],
    documentIds: string[],
    titles: string[],
  ): Promise<SetupFill> => {
    const { newDocumentIds, usableIds } = await readyDocuments(
      files,
      documentIds,
    );
    return { newDocumentIds, results: await fillPages(titles, usableIds) };
  };

  const pullContent = async (
    title: string,
    source: PullSource,
  ): Promise<HandbookPageFill | null> => {
    const { usableIds } =
      'file' in source
        ? await readyDocuments([source.file], [])
        : { usableIds: [source.documentId] };
    const results = await fillPages([title], usableIds);
    return results[title] ?? null;
  };

  /**
   * Imports run on a worker and there is no push event for them, so while
   * any import is still working its detail is re-read every few seconds.
   * When one finishes the tree is re-read too (its drafts have appeared).
   */
  const runningImportIds = (imports ?? [])
    .filter((i) => isImportRunning(i.status))
    .map((i) => i.id)
    .join(',');
  useEffect(() => {
    if (!runningImportIds) return;
    const ids = runningImportIds.split(',');
    let cancelled = false;
    const tick = async () => {
      const results = await Promise.allSettled(ids.map(fetchImport));
      if (cancelled) return;
      let finished = false;
      results.forEach((r) => {
        if (r.status !== 'fulfilled') {
          console.error('Error polling handbook import:', r.reason);
          return;
        }
        if (!isImportRunning(r.value.status)) finished = true;
        setImports((list) => (list ? upsertImport(list, r.value) : list));
      });
      if (finished) {
        try {
          setTree(await fetchTree());
        } catch (err) {
          console.error('Error reloading handbook tree:', err);
        }
      }
    };
    const timer = window.setInterval(tick, IMPORT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [runningImportIds, fetchImport, fetchTree]);

  /** Re-reads the tree and, if a page is open, that page and its history. */
  const refresh = useCallback(
    async (slug: string | null) => {
      const nextTree = await fetchTree();
      setTree(nextTree);
      loadSetup();
      if (!slug) return;
      try {
        setPage(await fetchPage(slug));
      } catch (err) {
        if (httpStatus(err) === 404) {
          setSelectedSlug(null);
          setPage(null);
          return;
        }
        throw err;
      }
    },
    [fetchTree, fetchPage, loadSetup],
  );

  const run = useCallback(
    async (
      key: string,
      action: () => Promise<string | void>,
      fallback: string,
    ) => {
      setFeedback(null);
      setBusyKey(key);
      try {
        const message = await action();
        if (message) setFeedback({ type: 'success', message });
        return true;
      } catch (err) {
        console.error(fallback, err);
        setFeedback({
          type: 'error',
          message: handbookErrorMessage(err, fallback),
        });
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  /** Like `run`, but errors stay inside the open confirmation dialog. */
  const runConfirmed = useCallback(
    async (
      key: string,
      action: () => Promise<string | void>,
      fallback: string,
    ) => {
      setConfirmError(null);
      setBusyKey(key);
      try {
        const message = await action();
        setConfirm(null);
        setFeedback(message ? { type: 'success', message } : null);
      } catch (err) {
        console.error(fallback, err);
        setConfirmError(handbookErrorMessage(err, fallback));
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const openPage = (slug: string) => {
    setAddingSection(false);
    setCreating(false);
    setFeedback(null);
    setSelectedSlug(slug);
    const section = tree ? sectionOf(tree.tree, slug) : null;
    if (section) setSelectedSection(section);
  };

  const openSection = (slug: string) => {
    setAddingSection(false);
    setCreating(false);
    setFeedback(null);
    setSelectedSlug(null);
    setSelectedSection(slug);
  };

  const saveSetup = async (
    patch: SaveHandbookSetupRequest,
  ): Promise<boolean> => {
    try {
      const { data } = await api.put<HandbookSetupResponse>(
        `${base}/setup`,
        patch,
        await authHeaders(),
      );
      setSetup(data.setup);
      setSetupError(null);
      return true;
    } catch (err) {
      console.error('Could not save the handbook setup.', err);
      setSetupError(
        handbookErrorMessage(err, 'Your answers could not be saved just now.'),
      );
      return false;
    }
  };

  const prefillSetup = async (
    files: File[],
    goals: SetupGoalId[],
  ): Promise<SetupPrefill> => {
    const { newDocumentIds, usableIds } = await readyDocuments(files, []);
    const results = await runFill({
      kind: 'prefill',
      goals,
      documentIds: usableIds,
    });
    if (results.kind !== 'prefill') {
      throw new HandbookFillError(
        'We couldn’t read your documents. Try again.',
      );
    }
    return { documentIds: newDocumentIds, results };
  };

  const previewSetup = async (question?: string) => {
    try {
      const { data } = await api.post<HandbookPreviewResponse>(
        `${base}/setup/preview`,
        question ? { question } : {},
        await authHeaders(),
      );
      return data.success ? data : null;
    } catch (err) {
      console.error('Could not preview a Handbook answer.', err);
      return null;
    }
  };

  const generateFromSetup = async (
    answers: HandbookSetupAnswers,
    documentIds: string[],
  ) => {
    setSetupError(null);
    setGenerating(true);
    try {
      const headers = await authHeaders();
      await api.put<HandbookSetupResponse>(
        `${base}/setup`,
        { step: 'generate', answers, documentIds },
        headers,
      );
      const { data } = await api.post<GenerateHandbookSetupResponse>(
        `${base}/setup/generate`,
        {},
        headers,
      );
      if (!data.success) throw new Error();
      setSetup(data.setup);
      setImports((list) => upsertImport(list ?? [], data.import));
    } catch (err) {
      console.error('Could not create the handbook.', err);
      setSetupError(
        handbookErrorMessage(err, 'Your Handbook could not be created.'),
      );
    } finally {
      setGenerating(false);
    }
  };

  const clearSetup = async () => {
    try {
      await api.delete(`${base}/setup`, await authHeaders());
      setSetup(null);
    } catch (err) {
      console.error('Could not clear the handbook setup.', err);
      setFeedback({
        type: 'error',
        message: handbookErrorMessage(err, 'Could not start again.'),
      });
    }
  };

  const handleCreate = (body: CreateHandbookPageRequest) =>
    run(
      'create',
      async () => {
        const { data } = await api.post<HandbookPageResponse>(
          `${base}/pages`,
          body,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        setTree(await fetchTree());
        setCreating(false);
        setSelectedSlug(null);
        setTab('drafts');
        return `Draft “${data.page.draft?.title ?? body.title}” created. Publish it when it is ready.`;
      },
      'Could not create the page.',
    );

  const handleSaveDraft = (patch: UpdateHandbookDraftRequest) => {
    if (!page) return;
    const slug = page.slug;
    run(
      'save',
      async () => {
        const { data } = await api.put<HandbookPageResponse>(
          `${base}/pages/${encodeURIComponent(slug)}/draft`,
          patch,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        setPage(data.page);
        setTree(await fetchTree());
        return 'Draft saved.';
      },
      'Could not save the draft.',
    );
  };

  const handlePublish = () => {
    if (!page) return;
    const slug = page.slug;
    run(
      'publish',
      async () => {
        const { data } = await api.post<PublishHandbookPagesResponse>(
          `${base}/pages/${encodeURIComponent(slug)}/publish`,
          {},
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        await refresh(slug);
        const published = data.pages.find((p) => p.slug === slug);
        const version = published?.live?.version;
        return version
          ? `Published v${version} of “${published?.live?.title}”. ${assistant.Who} ${assistant.agree('answers', 'answer')} from it now.`
          : 'Published.';
      },
      'Could not publish the page.',
    );
  };

  const handleDiscard = (target: HandbookPage) =>
    runConfirmed(
      'discard',
      async () => {
        const { data } = await api.delete<DiscardHandbookDraftResponse>(
          `${base}/pages/${encodeURIComponent(target.slug)}/draft`,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        await refresh(target.slug);
        return target.live
          ? 'Draft discarded. The live version is unchanged.'
          : 'Draft discarded.';
      },
      'Could not discard the draft.',
    );

  const handleArchive = (target: HandbookPage) =>
    runConfirmed(
      'archive',
      async () => {
        const { data } = await api.post<HandbookPageResponse>(
          `${base}/pages/${encodeURIComponent(target.slug)}/archive`,
          {},
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        await refresh(target.slug);
        return `“${target.live?.title ?? target.slug}” archived. ${assistant.Who} no longer ${assistant.agree('answers', 'answer')} from it.`;
      },
      'Could not archive the page.',
    );

  const handleRestore = (slug: string, version: HandbookPageVersion) =>
    runConfirmed(
      `restore:${version.version}`,
      async () => {
        const { data } = await api.post<HandbookPageResponse>(
          `${base}/pages/${encodeURIComponent(slug)}/versions/${version.version}/restore`,
          {},
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        await refresh(slug);
        return `Restored v${version.version} as v${data.page.live?.version ?? '?'}.`;
      },
      'Could not restore that version.',
    );

  if (!visible || !tree) return null;

  const busy = busyKey !== null;
  const pageTotal = tree.tree.reduce(
    (n, node) => n + sectionPages(node).length,
    0,
  );
  const publishedDates = allNodes(tree.tree)
    .map((n) => n.publishedAt)
    .filter((d): d is string => !!d)
    .sort();
  const lastPublished = publishedDates[publishedDates.length - 1] ?? null;
  const empty = tree.tree.length === 0;
  const generation =
    (setup?.importId && imports?.find((i) => i.id === setup.importId)) || null;
  const generationRunning = !!generation && isImportRunning(generation.status);
  const setupInProgress = !!setup && !setup.importId;

  const openSetup = () => {
    setSetupError(null);
    setFeedback(null);
    setSetupOpen(true);
  };

  const renderSetup = () =>
    setupOpen && (
      <HandbookSetupModal
        setup={setup}
        generation={generation}
        generating={generating}
        error={setupError}
        onSave={saveSetup}
        onPrefill={prefillSetup}
        onFill={fillOwnPages}
        loadDocuments={loadDocuments}
        onPreview={previewSetup}
        onGenerate={generateFromSetup}
        onClose={() => setSetupOpen(false)}
        onDone={() => {
          setSetupOpen(false);
          setSelectedSlug(null);
          setSelectedSection(null);
          refresh(null).catch((err) =>
            console.error('Error reloading handbook tree:', err),
          );
        }}
      />
    );

  const blankSlugs = new Set(
    allNodes(tree.tree)
      .filter((n) => !n.hasContent)
      .map((n) => n.slug),
  );
  const isBlank = (slug: string) => blankSlugs.has(slug);

  const renderBookActions = () => {
    const action = generationRunning
      ? { label: 'View progress', onClick: openSetup }
      : setupInProgress
        ? { label: 'Continue setting up', onClick: openSetup }
        : null;
    if (!action) return null;
    return (
      <div className='handbook-book-actions' data-testid='handbook-status'>
        <button
          type='button'
          className='handbook-setup-link'
          onClick={action.onClick}
        >
          {action.label}
        </button>
      </div>
    );
  };

  const renderFeedback = () =>
    feedback && (
      <p
        className={`organisation-feedback ${feedback.type}`}
        role={feedback.type === 'error' ? 'alert' : 'status'}
      >
        {feedback.message}
      </p>
    );

  const renderConfirm = () => {
    if (!confirm) return null;
    const close = () => setConfirm(null);
    switch (confirm.kind) {
      case 'discard':
        return (
          <HandbookConfirmModal
            title='Discard draft'
            heading={`Discard the draft of “${confirm.page.draft?.title ?? confirm.page.slug}”?`}
            confirmLabel='Discard draft'
            busyLabel='Discarding…'
            danger
            busy={busyKey === 'discard'}
            error={confirmError}
            onClose={close}
            onConfirm={() => handleDiscard(confirm.page)}
          >
            <p>
              {confirm.page.live
                ? 'The unpublished changes are thrown away; the live version stays as it is.'
                : 'This page has never been published, so discarding the draft removes it entirely.'}
            </p>
          </HandbookConfirmModal>
        );
      case 'archive':
        return (
          <HandbookConfirmModal
            title='Archive page'
            heading={`Archive “${confirm.page.live?.title ?? confirm.page.slug}”?`}
            confirmLabel='Archive'
            busyLabel='Archiving…'
            danger
            busy={busyKey === 'archive'}
            error={confirmError}
            onClose={close}
            onConfirm={() => handleArchive(confirm.page)}
          >
            <p>
              {assistant.Who} {assistant.agree('stops', 'stop')} answering from
              this page. Its versions are kept, and you can bring it back by
              restoring one from the section’s History tab.
            </p>
            {confirm.page.draft && <p>The pending draft is kept.</p>}
          </HandbookConfirmModal>
        );
      case 'restore':
        return (
          <HandbookConfirmModal
            title='Restore version'
            heading={`Restore v${confirm.version.version} of “${confirm.version.title}”?`}
            confirmLabel={`Restore v${confirm.version.version}`}
            busyLabel='Restoring…'
            busy={busyKey === `restore:${confirm.version.version}`}
            error={confirmError}
            onClose={close}
            onConfirm={() => handleRestore(confirm.slug, confirm.version)}
          >
            <p>
              Its content is published again as a new version and{' '}
              {assistant.who} {assistant.agree('answers', 'answer')} from it
              straight away. The page keeps its place in the tree.
            </p>
          </HandbookConfirmModal>
        );
      default:
        return null;
    }
  };

  const startAddingSection = () => {
    setSelectedSlug(null);
    setCreating(false);
    setFeedback(null);
    setAddingSection(true);
  };

  const handleAddSection = (section: NewSection) =>
    run(
      'add-section',
      async () => {
        const headers = await authHeaders();
        const { data } = await api.post<HandbookPageResponse>(
          `${base}/pages`,
          {
            title: section.title,
            bodyMarkdown: '',
            description: section.description || null,
            parentSlug: null,
          },
          headers,
        );
        try {
          for (const page of section.pages) {
            await api.post<HandbookPageResponse>(
              `${base}/pages`,
              {
                title: page.title,
                bodyMarkdown: page.fill?.bodyMarkdown ?? '',
                description: page.fill?.description ?? null,
                parentSlug: data.page.slug,
              },
              headers,
            );
          }
        } finally {
          setTree(await fetchTree());
        }
        setAddingSection(false);
        setSelectedSection(data.page.slug);
        setTab('drafts');
        return `Added “${section.title}” with ${plural(section.pages.length, 'draft page', 'draft pages')}.`;
      },
      'Could not add the section.',
    );

  const handleFillSection = (parentSlug: string, section: NewSection) =>
    run(
      'fill-section',
      async () => {
        const headers = await authHeaders();
        for (const page of section.pages) {
          if (page.slug) {
            if (!page.fill) continue;
            await api.put<HandbookPageResponse>(
              `${base}/pages/${encodeURIComponent(page.slug)}/draft`,
              {
                bodyMarkdown: page.fill.bodyMarkdown,
                ...(page.fill.description
                  ? { description: page.fill.description }
                  : {}),
              },
              headers,
            );
          } else {
            await api.post<HandbookPageResponse>(
              `${base}/pages`,
              {
                title: page.title,
                bodyMarkdown: page.fill?.bodyMarkdown ?? '',
                description: page.fill?.description ?? null,
                parentSlug,
              },
              headers,
            );
          }
        }
        setTree(await fetchTree());
        setFillingSection(null);
        setTab('drafts');
        return 'Your pages are saved as drafts.';
      },
      'Could not save the pages.',
    );

  const renderFillSection = () => {
    const node = tree?.tree.find((n) => n.slug === fillingSection);
    if (!node) return null;
    return (
      <HandbookSectionWizard
        busy={busyKey === 'fill-section'}
        taken={takenTitles(tree)}
        existing={{
          title: node.title,
          blankPages: sectionPages(node)
            .filter((n) => isBlank(n.slug))
            .map((n) => ({ slug: n.slug, title: n.title })),
        }}
        fill={async (files, titles) =>
          (await fillOwnPages(files, [], titles)).results
        }
        onCreate={(section) => handleFillSection(node.slug, section)}
        onClose={() => setFillingSection(null)}
      />
    );
  };

  const startCreating = (parentSlug: string | null = null) => {
    setAddingSection(false);
    setSelectedSlug(null);
    setFeedback(null);
    setCreateParent(parentSlug);
    setCreating(true);
  };

  const openRestore = (slug: string, version: HandbookPageVersion) => {
    setConfirmError(null);
    setConfirm({ kind: 'restore', slug, version });
  };

  const renderSectionEmpty = (
    Icon: FC<React.SVGProps<SVGSVGElement>>,
    title: string,
    text: string,
  ) => (
    <div className='handbook-section-empty'>
      <Icon aria-hidden='true' />
      <p className='handbook-section-empty-title'>{title}</p>
      <p className='handbook-section-empty-text'>{text}</p>
    </div>
  );

  const renderSectionHistory = () => {
    if (sectionHistory === null) {
      return (
        <p className='organisation-empty handbook-section-loading'>
          Loading history…
        </p>
      );
    }
    if (sectionHistory.length === 0) {
      return renderSectionEmpty(
        HistoryIcon,
        'No history yet',
        'Each time you publish a page in this section, its version shows up here so you can restore it.',
      );
    }
    return (
      <ul className='handbook-version-list' data-testid='handbook-history'>
        {sectionHistory.map(({ slug, version: v }) => {
          const isLive =
            v.status !== 'archived' &&
            !sectionHistory.some(
              (e) =>
                e.slug === slug && (e.version.version ?? 0) > (v.version ?? 0),
            );
          return (
            <li
              key={v.id}
              className='handbook-version-item'
              data-testid='handbook-version'
            >
              <div className='handbook-version-info'>
                <span className='handbook-version-title'>
                  {v.title} · v{v.version}
                  {isLive && <span className='handbook-badge live'>Live</span>}
                </span>
                <span className='handbook-meta'>
                  Published {formatDate(v.publishedAt ?? undefined)}
                </span>
              </div>
              {!isLive && (
                <button
                  type='button'
                  className='handbook-pill small'
                  disabled={busy}
                  onClick={() => openRestore(slug, v)}
                >
                  Restore
                </button>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderSection = (node: HandbookTreeNode) => {
    const pages = sectionPages(node);
    const published = pages.filter((n) => n.isPublished);
    const drafts = pages.filter((n) => n.hasDraft);
    const shown = tab === 'published' ? published : drafts;
    return (
      <div className='handbook-section' data-testid='handbook-section'>
        <div className='handbook-section-head'>
          <h3 className='handbook-section-title'>{node.title}</h3>
          <div className='handbook-section-head-actions'>
            <button
              type='button'
              className='handbook-pill'
              disabled={busy}
              onClick={() => setFillingSection(node.slug)}
            >
              Create pages from a document
            </button>
            <button
              type='button'
              className='handbook-pill primary'
              disabled={busy}
              onClick={() =>
                startCreating(node.children.length > 0 ? node.slug : null)
              }
            >
              Add page
            </button>
          </div>
        </div>
        <div className='organisation-tabs handbook-tabs' role='tablist'>
          {(
            [
              ['published', 'Published', published.length],
              ['drafts', 'Drafts', drafts.length],
              ['history', 'History', null],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type='button'
              role='tab'
              aria-selected={tab === id}
              className={`organisation-tab${tab === id ? ' active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
              {count !== null && (
                <span className='handbook-tab-count'>{count}</span>
              )}
            </button>
          ))}
        </div>
        {tab === 'history' ? (
          renderSectionHistory()
        ) : shown.length === 0 ? (
          tab === 'published' ? (
            renderSectionEmpty(
              FileIcon,
              'Nothing published yet',
              'Publish a page in this section and your team’s secretaries can start using it.',
            )
          ) : (
            renderSectionEmpty(
              EditIcon,
              'No drafts',
              'Pages you’re working on in this section show up here until you publish them.',
            )
          )
        ) : (
          <ul className='handbook-section-pages'>
            {shown.map((child) => {
              const preview = previews[child.slug];
              const text =
                (tab === 'published' ? preview?.live : preview?.draft) ||
                (tab === 'drafts' && !child.hasDraft ? preview?.live : '') ||
                '';
              return (
                <li key={child.slug}>
                  <button
                    type='button'
                    className={`handbook-page-card${isBlank(child.slug) ? ' blank' : ''}`}
                    disabled={busy}
                    onClick={() => openPage(child.slug)}
                  >
                    {isBlank(child.slug) ? (
                      <span className='handbook-blank-tag'>
                        Blank: your secretary won’t know this yet
                      </span>
                    ) : (
                      tab === 'drafts' && (
                        <span className='handbook-draft-tag'>
                          {child.isPublished
                            ? 'Changes not published yet'
                            : 'Draft, not published yet'}
                        </span>
                      )
                    )}
                    <span className='handbook-page-card-head'>
                      <span className='handbook-page-card-title'>
                        {child.title}
                      </span>
                      {preview?.createdAt && (
                        <span className='handbook-page-card-date'>
                          Created {formatCreated(preview.createdAt)}
                        </span>
                      )}
                    </span>
                    <span
                      className={`handbook-page-card-body${text ? '' : ' empty'}`}
                    >
                      {text ||
                        'No content yet. Open it to write it or pull it from a document.'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  if (empty && !creating) {
    const title = generationRunning
      ? 'Your Handbook is being created'
      : setupInProgress
        ? 'You’re part way through setting up your Handbook'
        : 'You’ve not created a Handbook yet';
    const text = generationRunning
      ? 'We’re writing your draft pages. Nothing goes live until you publish.'
      : setupInProgress
        ? 'Pick up where you left off. Your answers are saved.'
        : EMPTY_STATE_TEXT;
    return (
      <div className='handbook' data-testid='organisation-handbook'>
        <div className='organisation-card handbook-empty-card'>
          <h3 className='organisation-card-title'>Handbook</h3>
          <div className='handbook-empty'>
            <BookOpenIcon aria-hidden='true' />
            <p className='handbook-empty-title'>{title}</p>
            <p className='handbook-empty-text'>{text}</p>
            <div className='handbook-empty-actions'>
              <button
                type='button'
                className='handbook-pill primary'
                disabled={busy}
                onClick={openSetup}
              >
                {generationRunning
                  ? 'View progress'
                  : setupInProgress
                    ? 'Continue setting up'
                    : 'Create your Handbook'}
              </button>
              {setupInProgress && (
                <button
                  type='button'
                  className='handbook-pill'
                  disabled={busy}
                  onClick={clearSetup}
                >
                  Start again
                </button>
              )}
            </div>
          </div>
          {renderFeedback()}
        </div>
        {renderSetup()}
        {renderConfirm()}
      </div>
    );
  }

  return (
    <div className='handbook' data-testid='organisation-handbook'>
      {renderFeedback()}

      <div className='handbook-book' data-testid='handbook-book'>
        <div className='handbook-book-head'>
          <h3 className='handbook-book-title'>Handbook</h3>
          <div className='handbook-book-heading'>
            <div className='handbook-book-stats' data-testid='handbook-counts'>
              {lastPublished && (
                <span className='handbook-book-updated'>
                  Last updated {formatDay(lastPublished)}
                </span>
              )}
              <span className='handbook-book-stat'>
                <FolderIcon aria-hidden='true' />
                {plural(tree.tree.length, 'section', 'sections')}
              </span>
              <span className='handbook-book-stat'>
                <FileIcon aria-hidden='true' />
                {plural(pageTotal, 'page', 'pages')}
              </span>
            </div>
            {renderBookActions()}
          </div>
        </div>
        <div className='handbook-layout'>
          <nav className='handbook-tree' aria-label='Handbook sections'>
            <button
              type='button'
              className='handbook-pill handbook-add-section'
              disabled={busy}
              aria-pressed={addingSection}
              onClick={startAddingSection}
            >
              Add section
            </button>
            <div className='handbook-tree-divider' />
            {empty ? (
              <p className='organisation-empty'>No sections yet.</p>
            ) : (
              <ul className='handbook-tree-list'>
                {tree.tree.map((node) => {
                  const selected =
                    !addingSection && node.slug === activeSection?.slug;
                  const pages = sectionPages(node);
                  const blank = pages.filter((n) => isBlank(n.slug)).length;
                  const live = pages.filter(
                    (n) => n.isPublished && !isBlank(n.slug),
                  ).length;
                  const draftOnly = pages.length - live - blank;
                  return (
                    <li key={node.slug} data-testid='handbook-tree-node'>
                      <button
                        type='button'
                        className={`handbook-tree-item${selected ? ' selected' : ''}${blank ? ' has-blank' : ''}`}
                        disabled={busy}
                        aria-current={selected ? 'true' : undefined}
                        onClick={() => openSection(node.slug)}
                      >
                        <span className='handbook-tree-title'>
                          {node.title}
                        </span>
                        <span className='handbook-tree-meta'>
                          {live > 0 && (
                            <span className='handbook-tree-count'>
                              {plural(live, 'page', 'pages')}
                            </span>
                          )}
                          {draftOnly > 0 && (
                            <span className='handbook-tree-count'>
                              {plural(draftOnly, 'draft', 'drafts')}
                            </span>
                          )}
                          {pages.length === 0 && (
                            <span className='handbook-tree-count'>
                              No pages yet
                            </span>
                          )}
                          {blank > 0 && (
                            <span className='handbook-tree-blank'>
                              {blank} blank
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </nav>

          <div className='handbook-main'>
            {creating ? (
              <HandbookCreatePage
                loadDocuments={loadDocuments}
                pullContent={pullContent}
                parentOptions={parentCandidates(tree.tree, null)}
                teamAssistantsAvailable={teamAssistantsAvailable}
                busy={busyKey === 'create'}
                initialParentSlug={createParent}
                onCreate={handleCreate}
                onCancel={() => setCreating(false)}
              />
            ) : pageError ? (
              <div className='organisation-card'>
                <p className='organisation-feedback error' role='alert'>
                  {pageError}
                </p>
              </div>
            ) : page ? (
              <>
                <button
                  type='button'
                  className='handbook-back'
                  onClick={() => {
                    setSelectedSlug(null);
                    setPage(null);
                  }}
                >
                  <ArrowLeftIcon aria-hidden='true' />
                  Back to {activeSection?.title ?? 'Handbook'}
                </button>
                <HandbookPageEditor
                  key={`${page.slug}:${page.live?.id ?? ''}:${page.draft?.id ?? ''}:${page.draft?.updatedAt ?? ''}`}
                  page={page}
                  parentOptions={parentCandidates(tree.tree, page.slug)}
                  busyKey={busyKey}
                  onSaveDraft={handleSaveDraft}
                  onPublish={handlePublish}
                  onDiscardDraft={() => {
                    setConfirmError(null);
                    setConfirm({ kind: 'discard', page });
                  }}
                  onArchive={() => {
                    setConfirmError(null);
                    setConfirm({ kind: 'archive', page });
                  }}
                  loadDocuments={loadDocuments}
                  pullContent={pullContent}
                />
              </>
            ) : selectedSlug ? (
              <div className='organisation-card'>
                <p className='organisation-empty'>Loading page…</p>
              </div>
            ) : activeSection ? (
              renderSection(activeSection)
            ) : null}
          </div>
        </div>
      </div>

      {addingSection && (
        <HandbookSectionWizard
          busy={busyKey === 'add-section'}
          taken={takenTitles(tree)}
          fill={async (files, titles) =>
            (await fillOwnPages(files, [], titles)).results
          }
          onCreate={handleAddSection}
          onClose={() => setAddingSection(false)}
        />
      )}
      {renderFillSection()}
      {renderSetup()}
      {renderConfirm()}
    </div>
  );
};

export default OrganisationHandbook;
